import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { HiOutlineArrowLeft, HiCheck, HiX, HiRefresh } from 'react-icons/hi';
import moment from 'moment';

import { corporateService } from '../../services/corporation.service';
import GeneralModal from '../../helpers/GeneralModal';
import { createModal } from 'react-modal-promise';
import LogicContext from '../../Context/LogicContext';
import { GetRecoilCorporateApprovedList, SetRecoilCorporateApprovedList } from '../../store/corporateApproveList';
import CorporateAppScroll from './CorperateAppScroll';
import CorpFileviewer from './CorpFileViewer';

export default function CorporateAppDetailsPage() {
  const navigate = useNavigate();
  const { corporate_id } = useParams();
  const { pathname } = useLocation();
  const logicContext = useContext(LogicContext);

  const getRecoilData = GetRecoilCorporateApprovedList();
  const setRecoilData = SetRecoilCorporateApprovedList();

  const [corporateData, setCorporateData] = useState({});
  const [corporateDocs, setCorporateDocs] = useState([]);
  const [selectedList, setSelectedList] = useState([]);

  const myPromiseModal = createModal(GeneralModal);
  const isMounted = useRef(false);

  const currModuleId = pathname.split('/')[1];
  const currModuleName = pathname.split('/')[2];
  const currUserMenu = pathname.split('/')[3];

  useEffect(() => {
    isMounted.current = true;
    document.title = 'Corporate Approval | Details';
    fetchCorporateDetails(corporate_id);

    return () => {
      isMounted.current = false;
    };
  }, [corporate_id]);

  const fetchCorporateDetails = async id => {
    try {
      const res = await corporateService.getCorporateDetails(id);
      if (isMounted.current && res.data?.Corporate_Info?.[0]) {
        setCorporateData(res.data.Corporate_Info[0]);
        setCorporateDocs(res.data.Document_Info || []);
      }
    } catch (err) {
      logicContext.showToast('Failed to load corporate details', 'error');
    }
  };

  const goBack = () => navigate(-1);

  const refreshList = async () => {
    try {
      const res = await corporateService.getCorporateApprovedList();
      if (res?.data?.Display_Column) {
        setRecoilData(res.data);
      }
    } catch (err) {
      console.error('Failed to refresh corporate list', err);
    }
  };

  const confirmAndApprove = async () => {
    const result = await myPromiseModal({
      title: 'Approve Corporate',
      body: 'Do you want to approve this corporate account?',
      showInput: true,
      placeHolder: 'Approval remark (optional)',
    });

    if (result?.decision) {
      try {
        const res = await corporateService.approveOrCancelCorporateRequest(corporate_id, 6, result.message || 'Approved');
        const data = logicContext.httpResponseManager(res.data[0]);
        if (data?.message_id === 1) {
          await fetchCorporateDetails(corporate_id);
          await refreshList();
          logicContext.showToast(data.message_text, 'success');
        }
      } catch (err) {
        logicContext.showToast('Approval failed', 'error');
      }
    }
  };

  const confirmAndDecline = async () => {
    const result = await myPromiseModal({
      title: 'Decline Corporate',
      body: 'Do you want to decline this corporate request?',
      showInput: true,
      placeHolder: 'Decline reason (required)',
    });

    if (result?.decision) {
      if (!result.message?.trim()) {
        return logicContext.showToast('Remark is required for decline', 'warning');
      }

      try {
        const res = await corporateService.approveOrCancelCorporateRequest(corporate_id, 7, result.message);
        const data = logicContext.httpResponseManager(res.data[0]);
        if (data?.message_id === 1) {
          await fetchCorporateDetails(corporate_id);
          await refreshList();
          logicContext.showToast(data.message_text, 'success');
        }
      } catch (err) {
        logicContext.showToast('Decline failed', 'error');
      }
    }
  };

  const confirmAndResend = async () => {
    const result = await myPromiseModal({
      title: 'Resend Request',
      body: 'Do you want to resend the approval request?',
      showInput: true,
      placeHolder: 'Resend remark (optional)',
    });

    if (result?.decision) {
      try {
        const res = await corporateService.approveOrCancelCorporateRequest(corporate_id, 10, result.message || 'Resent for approval');
        const data = logicContext.httpResponseManager(res.data[0]);
        if (data?.message_id === 1) {
          await fetchCorporateDetails(corporate_id);
          await refreshList();
          logicContext.showToast(data.message_text, 'success');
        }
      } catch (err) {
        logicContext.showToast('Resend failed', 'error');
      }
    }
  };

  const canShowActions = () => {
    if (!getRecoilData?.approval_permission?.length) return false;

    const currentStatus = corporateData?.Corporate_Approval_Status_Id;
    return getRecoilData.approval_permission.some(perm => perm.Approval_Access_Id === currentStatus);
  };

  const statusStyles = {
    1: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    2: 'bg-rose-100 text-rose-800 border-rose-200',
    3: 'bg-amber-100 text-amber-800 border-amber-200',
    4: 'bg-blue-100 text-blue-800 border-blue-200',
    5: 'bg-purple-100 text-purple-800 border-purple-200',
    6: 'bg-gray-100 text-gray-700 border-gray-200',
  };

  const getStatusBadge = () => {
    const id = corporateData?.Corporate_Approval_Status_Id;
    const name = corporateData?.Corporate_Approval_Status_Name || 'Unknown';

    return (
      <span
        className={`inline-flex rounded-full border px-4 py-1.5 text-sm font-medium ${
          statusStyles[id] || 'border-gray-200 bg-gray-100 text-gray-700'
        }`}
      >
        {name}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-10 @container">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 pb-3 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <button onClick={goBack} className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900">
                <HiOutlineArrowLeft size={20} />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">Corporate Approval</h1>
              {corporateData?.Corporate_Short_Code && (
                <span className="font-mono text-sm text-gray-500">#{corporateData.Corporate_Short_Code}</span>
              )}
            </div>

            {canShowActions() && (
              <div className="flex items-center gap-2">
                <button
                  onClick={confirmAndApprove}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700"
                >
                  <HiCheck size={18} />
                  Approve
                </button>

                <button
                  onClick={confirmAndDecline}
                  className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rose-700"
                >
                  <HiX size={18} />
                  Decline
                </button>

                <button
                  onClick={confirmAndResend}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                >
                  <HiRefresh size={18} />
                  Resend
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl pt-6">
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Sidebar */}
          <div className="flex-shrink-0 lg:w-80 xl:w-96">
            <div className="sticky top-20 overflow-hidden rounded-xl bg-white">
              <CorporateAppScroll
                onSelection={list => setSelectedList(list.filter(i => i.checked))}
                viewItemDetails={id => {
                  navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${id}`);
                  fetchCorporateDetails(id);
                }}
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="rounded-xl border border-gray-200 bg-white p-6 md:p-8">
              {/* Header */}
              <div className="flex flex-col gap-6 border-b pb-8 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{corporateData?.Corporate_Name || 'Corporate Approval'}</h2>
                  <div className="mt-2 flex items-center gap-4 text-sm text-gray-600">
                    <span className="font-mono">ID: {corporateData?.Corporate_Id || '—'}</span>
                    <span>{corporateData?.Corporate_Type_Name || '—'}</span>
                  </div>
                </div>

                <div>{getStatusBadge()}</div>
              </div>

              <div className="mt-8 grid grid-cols-1 gap-8 text-sm md:grid-cols-2 lg:grid-cols-3">
                {/* Corporate Basics */}
                <div className="space-y-6">
                  <div>
                    <h3 className="mb-4 text-lg font-semibold text-gray-800">Corporate Details</h3>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-gray-600">Short Code</dt>
                        <dd className="font-medium">{corporateData?.Corporate_Short_Code || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-600">Phone</dt>
                        <dd className="font-medium">{corporateData?.Corporate_Contact_Person_Mobile || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-600">Registration</dt>
                        <dd className="font-medium">{moment(corporateData?.Encoded_Date).format('MMM D, YYYY • h:mm A') || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-600">Employees</dt>
                        <dd className="font-medium">{corporateData?.Number_Of_Officer_Name || '—'}</dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {/* Subscription */}
                <div className="space-y-6">
                  <div>
                    <h3 className="mb-4 text-lg font-semibold text-gray-800">Subscription</h3>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-gray-600">Plan</dt>
                        <dd className="font-medium">{corporateData?.Subscription_Name || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-600">Duration</dt>
                        <dd className="font-medium">{corporateData?.Subscription_Period || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-600">Agreement Date</dt>
                        <dd className="font-medium">{moment(corporateData?.Agreement_Date).format('MMM D, YYYY') || '—'}</dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {/* Contact & Address */}
                <div className="space-y-6">
                  <div>
                    <h3 className="mb-4 text-lg font-semibold text-gray-800">Contact & Address</h3>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-gray-600">Contact Person</dt>
                        <dd className="font-medium">{corporateData?.Corporate_Contact_Person || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-600">Email</dt>
                        <dd className="break-words font-medium">{corporateData?.Corporate_Email_Address || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-600">Phone</dt>
                        <dd className="font-medium">{corporateData?.Corporate_Contact_Person_Mobile || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-600">Address</dt>
                        <dd className="text-gray-800">
                          {corporateData?.Corporate_Adddress}, {corporateData?.City_Name}, {corporateData?.Country_Name}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>

              {/* Approval History */}
              <div className="mt-10 grid grid-cols-1 gap-8 border-t pt-8 md:grid-cols-2">
                <div>
                  <h3 className="mb-4 text-lg font-semibold text-gray-800">First Review</h3>
                  <dl className="space-y-3 text-sm">
                    <div>
                      <dt className="text-gray-600">Reviewed By</dt>
                      <dd className="font-medium">{corporateData?.First_Level_Encoded_By_Name || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-600">Date</dt>
                      <dd className="font-medium">
                        {corporateData?.First_Level_Encoded_Date
                          ? moment(corporateData.First_Level_Encoded_Date).format('MMM D, YYYY • h:mm A')
                          : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-600">Remark</dt>
                      <dd className="italic text-gray-700">{corporateData?.First_Level_Remark || 'No remark provided'}</dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h3 className="mb-4 text-lg font-semibold text-gray-800">Final Review</h3>
                  <dl className="space-y-3 text-sm">
                    <div>
                      <dt className="text-gray-600">Reviewed By</dt>
                      <dd className="font-medium">{corporateData?.Second_Level_Encoded_By_Name || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-600">Date</dt>
                      <dd className="font-medium">
                        {corporateData?.Second_Level_Encoded_Date
                          ? moment(corporateData.Second_Level_Encoded_Date).format('MMM D, YYYY • h:mm A')
                          : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-600">Remark</dt>
                      <dd className="italic text-gray-700">{corporateData?.Second_Level_Remark || 'No remark provided'}</dd>
                    </div>
                  </dl>
                </div>
              </div>

              {/* Documents */}
              {corporateDocs?.length > 0 && (
                <div className="mt-10 border-t pt-8">
                  <h3 className="mb-4 text-lg font-semibold text-gray-800">Attached Documents</h3>
                  <div className="overflow-hidden rounded-lg border bg-gray-50">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700">#</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700">File Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {corporateDocs.map((doc, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{index + 1}</td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                              {doc?.File_Name || 'Unnamed file'}
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm">
                              <CorpFileviewer fileSource={doc?.File_URL} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
